import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'

import { useForm, UseFormReturn } from '../../../../../hooks'
import { getCurrentLocale } from '../../../../../utils'
import { CustomerModalSubmitActionType } from '../../../enums/customerModalSubmitActionType'
import { CustomerType } from '../../../enums/customerType'
import { useCustomerFormSubmit } from '../../../hooks/useCustomerFormSubmit'
import { Contact } from '../../../types/contact'
import { ContactPerson } from '../../../types/contactPerson'
import { CustomerForm } from '../../../types/customerForm'
import { createContactPersonsFromFormData } from '../../../utils/createContactPersonsFromFormData'
import { CustomerOptionalItem } from '../enums/customerOptionalItem'
import { useAddContactPerson } from '../hooks/useAddContactPerson/useAddContactPerson'
import { useGetCustomerFormValues } from '../hooks/useGetCustomerFormValues'
import { useUpdateContactPerson } from '../hooks/useUpdateContactPerson/useUpdateContactPerson'
import { CustomerFormInitialValues } from '../types/customerForm/customerFormInitialValues'
import { CustomerFormSchema } from '../types/customerForm/customerFormSchema'
import { getCustomerFormDefaultValues } from '../utils/customerForm/getCustomerFormDefaultValues'
import { getCustomerFormSchema } from '../utils/customerForm/getCustomerFormSchema'
import { getCustomerFormInitialOptionalItems } from '../utils/getCustomerFormInitialOptionalItems'

interface ContextState {
  activeOptionalFormItems: CustomerOptionalItem[]
  addContactPerson: () => boolean
  addOptionalFormItem: (optionalItem: CustomerOptionalItem) => void
  contactPersons: ContactPerson[]
  contactPersonIdToEdit: string | null
  customerForm: UseFormReturn<CustomerFormSchema>
  customerType: CustomerType
  isLoading: boolean
  isSubmitting: boolean
  removeOptionalFormItem: (optionalItem: CustomerOptionalItem) => void
  setContactPersonIdToEdit: (contact: string | null) => void
  submitActionType?: CustomerModalSubmitActionType
  submitForm: () => void
  updateContactPerson: (contactId: string) => boolean
}

const CustomerFormContext = createContext<ContextState | undefined>(undefined)

interface CustomerFormProviderProps {
  children: ReactNode
  customer?: Contact
  initialCustomerType: CustomerType
  initialValues?: CustomerFormInitialValues
  onSubmit: (customer: Contact) => void
  submitActionType?: CustomerModalSubmitActionType
}

export const CustomerFormContextProvider = ({
  children,
  customer,
  initialCustomerType,
  initialValues,
  onSubmit,
  submitActionType,
}: CustomerFormProviderProps): ReactElement => {
  const areCustomerValuesLoaded = useRef(false)
  const { t } = useTranslation()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const [activeOptionalFormItems, setActiveOptionalFormItems] = useState<CustomerOptionalItem[]>([])
  const [contactPersonIdToEdit, setContactPersonIdToEdit] = useState<string | null>(null)
  const { customerValues, isLoading: isLoadingCustomerValues } = useGetCustomerFormValues({ customer, initialValues })

  const locale = getCurrentLocale()

  const customerForm = useForm<CustomerFormSchema>({
    defaultValues: getCustomerFormDefaultValues(),
    validationSchema: getCustomerFormSchema(isFrenchComplianceMode, t),
  })

  const { control, Form, handleSubmit, reset: resetForm, setValue } = customerForm
  const customerType = customerForm.watch('customerType')

  const {
    submit,
    isLoading: isLoadingCustomerForm,
    isSubmitting,
  } = useCustomerFormSubmit({ onSubmit, submitActionType })

  const isLoading = isLoadingCustomerValues || isLoadingCustomerForm

  const { fields: contactPersons } = useFieldArray({
    control,
    name: 'contactPersons',
  })

  const { addContactPerson } = useAddContactPerson({
    customerForm,
  })

  const { updateContactPerson } = useUpdateContactPerson({
    customerForm,
    contactPersons,
  })

  const handleCustomerFormSubmit = useCallback(
    async (formData: CustomerForm) => {
      const contactPersons = createContactPersonsFromFormData(formData)

      await submit({
        ...formData,
        contactPersons,
      })
    },
    [submit],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleCustomerFormSubmit)()
  }, [handleSubmit, handleCustomerFormSubmit])

  const addOptionalFormItem = useCallback(
    (optionalItem: CustomerOptionalItem) => {
      const updatedList = [...activeOptionalFormItems, optionalItem]
      setActiveOptionalFormItems(updatedList)
    },
    [activeOptionalFormItems],
  )

  const removeOptionalFormItem = useCallback(
    (optionalItem: CustomerOptionalItem) => {
      setValue(optionalItem, undefined)
      const updatedList = activeOptionalFormItems.filter((item) => item !== optionalItem)
      setActiveOptionalFormItems(updatedList)
    },
    [activeOptionalFormItems, setValue],
  )

  useEffect(() => {
    if (!areCustomerValuesLoaded.current && customerValues && !isLoadingCustomerValues) {
      if (!customerValues.customerType) {
        customerValues.customerType = initialCustomerType
      }

      resetForm(customerValues)
      areCustomerValuesLoaded.current = true

      const initialOptionalItems = getCustomerFormInitialOptionalItems({
        customerCurrency: customerValues.currency,
        customerLanguage: customerValues.language,
        userLocale: locale,
      })
      setActiveOptionalFormItems(initialOptionalItems)
    }
  }, [customerValues, initialCustomerType, isLoadingCustomerValues, locale, resetForm])

  return (
    <CustomerFormContext.Provider
      value={{
        activeOptionalFormItems,
        addContactPerson,
        addOptionalFormItem,
        contactPersons,
        contactPersonIdToEdit,
        customerForm,
        customerType,
        isLoading,
        isSubmitting,
        removeOptionalFormItem,
        setContactPersonIdToEdit,
        submitActionType,
        submitForm,
        updateContactPerson,
      }}
    >
      <Form>{children}</Form>
    </CustomerFormContext.Provider>
  )
}

export const useCustomerForm = () => {
  const context = useContext(CustomerFormContext)

  if (!context) {
    throw new Error('CustomerFormContextProvider is missing in the module!')
  }

  return context
}
